import React, { useState, useEffect } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import styled from 'styled-components';
import { RotatingLines } from 'react-loader-spinner';
import { imageCache } from '../services/cache';

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100%;

  /* Target the wrapper span generated by react-lazy-load-image-component */
  & > span {
    width: 100% !important;
    height: 100% !important;
    display: block !important;
  }

  /* Target the image itself */
  img {
    width: 100% !important;
    height: 100% !important;
    object-fit: ${({ $objectFit }) => $objectFit || 'cover'};
    display: block;
  }
`;

const LoaderContainer = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: #f0f0f0;
  z-index: 1;
`;

const LazyImage = ({ src, alt, objectFit, ...props }) => {
  const [loading, setLoading] = useState(true);
  const [imageSrc, setImageSrc] = useState(null);

  useEffect(() => {
    let active = true;
    let objectUrl = null;

    const loadSrc = async () => {
      if (src && src.startsWith('data:')) {
        setImageSrc(src);
        setLoading(false);
        return;
      }

      const fullUrl = src && src.startsWith('/') 
        ? `${process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || ''}${src}` 
        : src;
      
      if (!fullUrl) {
        if (active) setLoading(false);
        return;
      }

      try {
        const blob = await imageCache.getItem(fullUrl);
        if (blob && active) {
          objectUrl = URL.createObjectURL(blob);
          setImageSrc(objectUrl);
        } else if (active) {
          setImageSrc(fullUrl);
        }
      } catch (err) {
        console.error('Error loading image from cache:', err);
        if (active) setImageSrc(fullUrl);
      }
    };

    loadSrc();

    return () => {
      active = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [src]);

  const handleImageLoad = () => {
    setLoading(false);
  };

  if (!imageSrc) {
      return (
        <ImageWrapper $objectFit={objectFit}>
             <LoaderContainer>
                <RotatingLines strokeColor="grey" width="30" />
             </LoaderContainer>
        </ImageWrapper>
      );
  }

  return (
    <ImageWrapper $objectFit={objectFit}>
      {loading && (
        <LoaderContainer>
          <RotatingLines strokeColor="grey" width="30" />
        </LoaderContainer>
      )}
      <LazyLoadImage
        alt={alt}
        src={imageSrc}
        effect="blur"
        afterLoad={handleImageLoad}
        onError={(e) => {
          console.error('LazyImage load error:', imageSrc, e);
          setLoading(false);
        }}
        wrapperProps={{
            style: {width: '100%', height: '100%', display: 'block'}
        }}
        {...props}
      />
    </ImageWrapper>
  );
};

export default LazyImage;
